import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    ContentBanner,
    Button,
    Icon,
    pxToRem,
    ContentBannerProps,
    ButtonProps,
    Icons,
} from '@modernhealth/vital-components';

import { colors } from '@modernhealth/vital-foundation';

import { navigationService } from 'src/config/rootService';
import { BannerContentFragment } from 'src/modules/dashboard/documents/getHomeScreen.generated';
import { calculateDailyPauseStreakCTA } from '../util/calculateDailyPauseStreakCTA';
import { useAnalytics } from '@modernhealth/web-analytics';
import { ScheduledContentContentType } from 'src/generated/webapp_gql';
import { addDeviceResolutionToImageURLParams } from 'src/shared/helpers/urlHelper';

export interface ScheduledContentCardProps {
    scheduledContent: BannerContentFragment;
    dayStreakCount: number;
    weekStreakCount: number;
    showStreakInfo?: boolean;
    onActionButtonClick: (uuid: string) => void;
}

export const ScheduledContentCard: React.FC<ScheduledContentCardProps> = ({
    scheduledContent,
    dayStreakCount,
    weekStreakCount,
    showStreakInfo,
    onActionButtonClick,
}) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const {
        bannerTitle,
        bannerContent,
        bannerImage,
        uuid,
        accentColor,
        isCompleted,
        titleColor,
        contentType,
    } = scheduledContent;

    const dailyPauseStreakCTA = calculateDailyPauseStreakCTA(dayStreakCount, weekStreakCount, t);
    const isPathways = contentType === ScheduledContentContentType.Pathways;
    const ctaButtonTitleMap = {
        [ScheduledContentContentType.Pathways]: t('pathways:pathways_page.pathways.explore'),
        [ScheduledContentContentType.DailyPause]: isCompleted
            ? t('dailyPause:content_banner.completed_button_text')
            : t('dailyPause:content_banner.button_text'),
    };
    const buttonVariantMap = {
        [ScheduledContentContentType.Pathways]: 'primary',
        [ScheduledContentContentType.DailyPause]: 'secondary',
    };
    const startIconMap = {
        [ScheduledContentContentType.Pathways]: undefined,
        [ScheduledContentContentType.DailyPause]: isCompleted ? 'checkmark' : undefined,
    };
    const ctaButtonTitle = ctaButtonTitleMap[contentType];
    const buttonVariant = buttonVariantMap[contentType] as ButtonProps['variant'];
    const startIcon = startIconMap[contentType] as Icons;
    const subtitle =
        isPathways && scheduledContent.badgeTitle ? scheduledContent.badgeTitle : undefined;
    const contentBannerImage = bannerImage
        ? addDeviceResolutionToImageURLParams(bannerImage)
        : undefined;
    const renderStreakInfo = () => (
        <StreakCta
            variant="outline"
            onClick={() => {
                navigationService.navigate('/pause/stats');
                analytics.trackClick('homepage: hero: daily pause streaks', {
                    home_section: 'hero',
                    cta_copy: dailyPauseStreakCTA,
                });
            }}
        >
            🔥 {dailyPauseStreakCTA} <Icon name="chevron-right" />
        </StreakCta>
    );

    return (
        <StyledContentBanner
            title={bannerTitle}
            description={bannerContent}
            image={contentBannerImage}
            buttonTitle={ctaButtonTitle}
            onClick={() => onActionButtonClick(uuid)}
            backgroundColor={accentColor ? `#${accentColor}` : colors.white}
            buttonProps={{
                icon: startIcon,
                variant: buttonVariant,
            }}
            footer={showStreakInfo ? renderStreakInfo() : null}
            titleColor={titleColor ?? undefined}
            isPathways={isPathways}
            subtitle={subtitle}
        />
    );
};

const StreakCta = styled(Button)({
    border: 'none',
    color: colors.white,
    paddingLeft: 0,
    marginTop: pxToRem(24),
});

// ContentBanner has theme props which is string and it conflicts with emotion theme props
const StyledContentBanner = styled(ContentBanner)<
    ContentBannerProps & { titleColor?: string; isPathways: boolean }
>(({ titleColor, isPathways }) => {
    const color = titleColor ? `#${titleColor}` : undefined;
    const img = isPathways ? { maxWidth: '47%', width: '47%' } : undefined;

    return {
        h2: { color },
        h4: { color },
        p: { color },
        img,
    };
});
